import type { Op } from 'quill/core';
import { Delta } from 'quill/core';
import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import Page from '../../app/layout/Page';
import Button from '../../components/Button';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import QuillContext from '../../editor/QuillContext';
import { gql } from '../../graphql/generated/gql';
import InternalTopbar from '../../topbar/InternalTopbar';
import postPath from '../../utils/url/postPath';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';
import { toAnnotationVersionChange } from './AnnotationVersionChangeCard';
import type { BasePostHistoryViewMagneticWallVersionChanges } from './PostHistoryView';
import {
  BasePostHistoryView,
  useCalculatedVersionChanges,
} from './PostHistoryView';
import VersionChanges from './VersionChanges';

const COMPARE_POSTS_VIEW_QUERY = gql(/** @complexity 29 */ `
  query ComparePostsView($basePostId: ID!, $comparePostId: ID!) {
    basePost: post(id: $basePostId) {
      id
      content(isLatest: true) {
        id
        delta
      }
      annotations {
        id
        ...BasePostHistoryView_annotation
      }
      ...BasePostHistoryView_post
    }
    comparePost: post(id: $comparePostId) {
      id
      title
      editedAt
      content(isLatest: true) {
        id
        delta
      }
      annotations {
        id
        ...BasePostHistoryView_annotation
      }
    }
    entityAssetToken(entityId: $basePostId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

function useDiff(baseContent?: Op[], compareContent?: Op[]) {
  return useMemo(() => {
    const diff = new Delta(baseContent)
      .diff(new Delta(compareContent))
      .map((op) => {
        if (op.insert) {
          return {
            ...op,
            attributes: { ...op.attributes, version: 1, diff: 'inserted' },
          };
        }

        if (op.delete) {
          return {
            retain: op.delete,
            attributes: { ...op.attributes, version: 1, diff: 'deleted' },
          };
        }

        return op;
      });
    return new Delta(diff);
  }, [baseContent, compareContent]);
}

interface ComparePostsViewInnerProps {
  basePostId: string;
  comparePostId: string;
}

const ComparePostsViewInner = ({
  basePostId,
  comparePostId,
}: ComparePostsViewInnerProps) => {
  const { data } = useQuery(COMPARE_POSTS_VIEW_QUERY, {
    variables: { basePostId, comparePostId },
  });

  const basePost = data?.basePost;
  const comparePost = data?.comparePost;

  const diff = useDiff(basePost?.content.delta, comparePost?.content.delta);

  const { content, editVersionChanges } = useCalculatedVersionChanges({
    diff: diff.ops,
    snapshot: basePost?.content.delta,
  });

  const magneticWallVersionChanges: BasePostHistoryViewMagneticWallVersionChanges[] =
    useMemo(() => {
      const { allAnnotationIds } = collectThreadAndAnnotationIds(
        content?.ops ?? [],
      );

      return [
        ...(basePost?.annotations
          .map((annotation) => toAnnotationVersionChange(annotation))
          // When the base post and the compare post have the same content,
          // but the base post has an annotation on it, then the diff will
          // nullify the annotation (since it doesn't exist in the compare post).
          // This would lead to annotations without context.
          // Because we don't want to show annotations without context when
          // comparing posts, we filter them out.
          .filter((annotation) => allAnnotationIds.has(annotation.id)) ?? []),
        ...(comparePost?.annotations.map((annotation) =>
          toAnnotationVersionChange(annotation, { isNew: true }),
        ) ?? []),
      ];
    }, [content?.ops, basePost?.annotations, comparePost?.annotations]);

  if (!basePost || !comparePost) return null;

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <Page
        topbar={
          <InternalTopbar
            postActions={
              <>
                <Button variant="secondary" to={postPath(basePost)}>
                  Base Post
                </Button>
                <Button variant="secondary" to={postPath(comparePost)}>
                  Compare Post
                </Button>
              </>
            }
          />
        }
      >
        <BasePostHistoryView
          post={basePost}
          editedAt={comparePost.editedAt}
          editVersionChanges={editVersionChanges}
          content={content}
          magneticWallVersionChanges={magneticWallVersionChanges}
          topbar={
            <VersionChanges.Topbar>
              <VersionChanges.Meta>
                Comparing to &quot;{comparePost.title}&quot;
              </VersionChanges.Meta>
            </VersionChanges.Topbar>
          }
        />
      </Page>
    </AssetTokenProvider>
  );
};

interface ComparePostsViewProps {
  basePostId: string;
  comparePostId: string;
}

const ComparePostsView = ({
  basePostId,
  comparePostId,
}: ComparePostsViewProps) => {
  return (
    <QuillContext>
      <ComparePostsViewInner
        basePostId={basePostId}
        comparePostId={comparePostId}
      />
    </QuillContext>
  );
};

export default ComparePostsView;
